import { BasicInfo, Language, License, ProviderProfile, UserProfile } from "@brands/services/identity/types/UserProfile";
import { ProviderMyProfileSubmit } from "../utils/types";
import { parsePhoneNumber } from "libphonenumber-js";
import moment from "moment";

export function hasBasicInfoChanged(currentUser: UserProfile, newData: BasicInfo): boolean {
    const normalizeLanguages = (langs: Language[] | string[] = []) =>
      langs.map((lang: any) => (typeof lang === 'string' ? lang : lang.id?.toString())).sort();

    const currentLanguages = normalizeLanguages(currentUser.basic_info?.languages || []);
    const newLanguages = normalizeLanguages(newData.languages || []);

    return (
      currentUser.basic_info.first_name !== newData.first_name ||
      currentUser.basic_info.middle_name !== newData.middle_name ||
      currentUser.basic_info.last_name !== newData.last_name ||
      currentUser.basic_info.gender !== newData.gender ||
      currentUser.basic_info.pronouns !== newData.pronouns ||
      currentUser.basic_info.gender_identity !== newData.gender_identity ||
      JSON.stringify(currentLanguages) !== JSON.stringify(newLanguages)
    );
  }

  export function hasContactInfoChanged(currentUser: UserProfile, newData: ProviderMyProfileSubmit): boolean {
    const normalizedPhone = newData.phone_number ? parsePhoneNumber(newData.phone_number, 'US')?.format('E.164') : null;

    const addressChanged =
      currentUser.address?.address1 !== newData.address1 ||
      currentUser.address?.address2 !== newData.address2 ||
      currentUser.address?.city !== newData.city ||
      currentUser.address?.state !== newData.state ||
      currentUser.address?.zip_code !== newData.zip_code;

    const phoneChanged = currentUser.phone_number !== normalizedPhone;

    return phoneChanged || addressChanged;
  }

  export function hasProviderProfileChanged(currentProfile: ProviderProfile | null, updated: any, reviewProfile: boolean | undefined): boolean {
    if (!currentProfile) return true;

    const isDifferentArray = (a: string[] = [], b: string[] = []) => {
      if (a.length !== b.length) return true;
      const sortedA = [...a].sort();
      const sortedB = [...b].sort();
      return !sortedA.every((val, idx) => val === sortedB[idx]);
    };

    return (
      currentProfile.biography !== updated.biography ||
      currentProfile.additional_credentials !== updated.additional_credentials ||
      isDifferentArray(currentProfile.education, updated.education) ||
      isDifferentArray(currentProfile.primary_specialty, updated.primary_specialty) ||
      isDifferentArray(currentProfile.secondary_specialty, updated.secondary_specialty) ||
      (reviewProfile && currentProfile.national_provider_id !== updated.national_provider_id) ||
      (reviewProfile && currentProfile.credentials !== updated.credentials) ||
      false
    );
  }

  export function haveInsuranceCarriersChanged(
    a: {
      insurance_carrier_id?: number;
      insurance_carrier?: { id: number };
      effective_date: string;
      recredential_date: string;
    }[],
    b: {
      insurance_carrier_id?: number;
      insurance_carrier?: { id: number };
      effective_date: string;
      recredential_date: string;
    }[],
  ): boolean {
    if (a.length !== b.length) return true;

    const normalize = (carrier: any) => ({
      insurance_carrier_id: carrier.insurance_carrier_id ?? carrier.insurance_carrier?.id ?? -1,
      effective_date: moment(carrier.effective_date).format('YYYY-MM-DD'),
      recredential_date: moment(carrier.recredential_date).format('YYYY-MM-DD'),
    });

    const normalizedA = a.map(normalize).sort((x, y) => x.insurance_carrier_id - y.insurance_carrier_id);
    const normalizedB = b.map(normalize).sort((x, y) => x.insurance_carrier_id - y.insurance_carrier_id);

    return !normalizedA.every(
      (val, idx) =>
        val.insurance_carrier_id === normalizedB[idx].insurance_carrier_id &&
        val.effective_date === normalizedB[idx].effective_date &&
        val.recredential_date === normalizedB[idx].recredential_date,
    );
  }

  export function hasLicenseChanged(current: License, updated: License): boolean {
    const formatDate = (date: string) => moment(date, ['YYYY-MM-DD', 'MM/DD/YYYY']).format('YYYY-MM-DD');

    return (
      current.state !== updated.state ||
      current.number !== updated.number ||
      formatDate(current.expiry_date) !== formatDate(updated.expiry_date) ||
      haveInsuranceCarriersChanged(current.insurance_carriers ?? [], updated.insurance_carriers ?? [])
    );
  }